import * as React from "react";
import { merge, $ } from "glamor";
import { theme } from "styles/theme";
import { Slide, PrevArrow, NextArrow } from "components/product/summary";
import Label from "components/form/label";
import { formatMessage } from "lib/globalize-util";
import Slider, { Settings } from "react-slick";
import "styles/slick/slick-theme.styl";
import "styles/slick/slick.styl";
import { Item } from "constants/product-collection";
import { List } from "immutable";
import { getStore } from "store";
import { getBaseSwatchStyles } from "helpers/swatch-utils";
import { ICollectionSwatch } from "constants/color-swatches";
import { BrandRecord } from "records/brand";
import { getProductImages } from "./helpers";
import { imageFallback } from "helpers/product-helpers";

interface IColorVariantsProps {
    productId: string;
    variantColorIds: Array<{ string_value: string }>;
    globalProducts: List<any>;
    onSelectColorVariant: (prod: { _id: string }) => void;
    colorSwatches: { [key: string]: ICollectionSwatch };
    brand: BrandRecord;
}

const MAX_TO_SHOW = 4;

export const ColorVariants: React.FunctionComponent<IColorVariantsProps> = ({
    productId,
    variantColorIds,
    globalProducts,
    onSelectColorVariant,
    colorSwatches,
    brand,
}) => {
    const handleClick = React.useCallback(
        ({ currentTarget }) => {
            const _productId = currentTarget.getAttribute("data-id");
            onSelectColorVariant({ _id: _productId });
        },
        [onSelectColorVariant]
    );

    const variants = React.useMemo(() => {
        const _variants = [];

        const { scope } = getStore().getState();

        variantColorIds.forEach((colorId) => {
            const variantProduct = globalProducts.find(
                (prod) => prod.getIn(Item.FIELDS.PRODUCT_ID) === colorId.string_value
            );
            const isCurrentProduct = productId === colorId.string_value;
            const [imageUrl = imageFallback] = getProductImages(brand, variantProduct, 102);

            const color = variantProduct && variantProduct.getIn(Item.FIELDS.COLOR);
            const swatch = colorSwatches[colorId.string_value];
            const style = getBaseSwatchStyles(colorSwatches[colorId.string_value]);

            const slide = (
                <Slide
                    scope={scope}
                    variantId={colorId.string_value}
                    swatch={swatch}
                    currentProduct={isCurrentProduct}
                    label={swatch?.label || color}
                    imageUrl={imageUrl}
                    onClick={handleClick}
                    key={colorId.string_value}
                    forceProductImage={true}
                >
                    {style && <div className="hexSwatch" style={style} />}
                </Slide>
            );
            if (variantProduct) {
                if (isCurrentProduct) {
                    _variants.unshift(slide);
                } else {
                    _variants.push(slide);
                }
            }
        });

        return _variants;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variantColorIds, globalProducts, productId, handleClick, colorSwatches]);

    const styles = React.useMemo(() => {
        return merge(
            $(" .singleColorBox", {
                position: "relative",
                paddingLeft: "20px",
                fontSize: "15px",
                lineHeight: "40px",
            }),
            $(" .flexContainer", {
                display: "flex",
                alignItems: "center",
            }),
            $(" .label", {
                fontWeight: "bold !important",
                marginBottom: "10px !important",
            }),
            $(" .variantCount", {
                color: theme.palette.canvasColor,
                backgroundColor: theme.palette.darkGrey,
                padding: "0 10px",
                borderRadius: "11px",
                lineHeight: "20px",
                marginLeft: "10px",
                marginBottom: "10px",
                fontSize: "12px",
            }),
            $(" .sliderContainer", {
                overflow: "hidden",
                padding: "0 17px",
                maxWidth: variants.length < MAX_TO_SHOW ? `${variants.length * 100}px` : "400px",
            }),
            $(" .current", {
                color: "black",
            }),
            $(" .variantColor", {
                width: "65px",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                textAlign: "center",
                fontSize: "12px",
                height: "34px",
            }),
            $(" .slide", {
                cursor: "pointer",
            }),
            $(" .slideImage", {
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center center",
                width: "65px",
                height: "102px",
                backgroundSize: "65px",
            }),
            $(" .carouselIcon", {
                display: "block",
            }),
            $(" .currentSelectedColor", {
                borderBottom: `3px ${theme.palette.accent4Color} solid`,
            }),
            $(" .hexSwatch", {
                height: "25px",
                width: "25px",
                borderRadius: "50%",
                border: `solid 1px ${theme.palette.lightGrey}`,
                position: "absolute",
                top: "72px",
                marginLeft: "55px",
            })
        );
    }, [variants]);

    const settings = React.useMemo<Settings>(
        () => ({
            dots: false,
            lazyLoad: true,
            infinite: false,
            speed: 500,
            slidesToShow: Math.min(MAX_TO_SHOW, variants.length),
            slidesToScroll: Math.min(MAX_TO_SHOW, variants.length),
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
            responsive: [
                {
                    breakpoint: 600,
                    settings: {
                        slidesToShow: Math.min(4, variants.length),
                        slidesToScroll: Math.min(4, variants.length),
                    },
                },
            ],
        }),
        [variants]
    );

    return (
        <div {...styles}>
            <div className="flexContainer">
                <Label className="label">{formatMessage("Available Colors")}</Label>
                <span className="variantCount">{variants.length}</span>
            </div>
            <div className="sliderContainer">
                <Slider {...settings}>{variants}</Slider>
            </div>
        </div>
    );
};
